// Rich-text (TipTap HTML) helpers shared by the editor and the pages that
// render what it saves.

// Turns plain text into simple paragraph HTML so it can be dropped into the
// TipTap document — used both for AI rewrite responses (the enhancement
// endpoint only understands plain text, so round-tripping real HTML through
// it isn't attempted) and for legacy plain-text field values (existing
// drafts, the default introduction/conclusion copy), whose literal "\n\n"
// paragraph breaks would otherwise collapse into one run-on line once
// rendered inside a single <p>. Lines that start with a bullet (CV imports
// write "• " highlights; people type "- ") become a real list, the same lines
// MarkdownList shows as one.
const BULLET_LINE = /^[*•◦▪‣-]\s+/;

export function plainTextToHtml(text: string) {
  const escape = (value: string) =>
    value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
  const paragraphs = text
    .split(/\n{2,}/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  if (paragraphs.length === 0) return "<p></p>";
  return paragraphs
    .map((paragraph) => {
      const blocks: string[] = [];
      let lines: string[] = [];
      let bullets: string[] = [];
      const flush = () => {
        if (lines.length) blocks.push(`<p>${lines.map(escape).join("<br>")}</p>`);
        if (bullets.length) blocks.push(`<ul>${bullets.map((item) => `<li><p>${escape(item)}</p></li>`).join("")}</ul>`);
        lines = [];
        bullets = [];
      };
      for (const raw of paragraph.split("\n")) {
        const line = raw.trim();
        if (BULLET_LINE.test(line)) {
          if (lines.length) flush();
          bullets.push(line.replace(BULLET_LINE, ""));
        } else {
          if (bullets.length) flush();
          lines.push(line);
        }
      }
      flush();
      return blocks.join("");
    })
    .join("");
}

// A value is only trusted as real HTML once it's actually been through this
// editor (or the backend) — a bare "<" with no matching close tag from
// plain prose (e.g. "3 < 5 candidates") would otherwise misfire.
const isHtml = (text: string) => /<\/?[a-z][\s\S]*>/i.test(text);

export const toEditorHtml = (value?: string) => {
  if (!value) return "";
  return isHtml(value) ? value : plainTextToHtml(value);
};

// TipTap leaves a "<p></p>" (or similar tags-only markup) behind after all
// text is deleted — that's a non-empty, truthy string, so callers checking
// `Boolean(value)` to decide whether a field "has content" get the wrong
// answer once a user has typed and then cleared it. Strip tags and check
// for real text instead.
export const isHtmlEmpty = (value?: string) => {
  if (!value) return true;
  return value.replace(/<[^>]*>/g, "").trim().length === 0;
};
